/**
 * Kontekst WiseTable – udostępnia stan i handlery tabeli bez prop drillingu.
 * Komponenty potomne (TableTopToolbar, TableBottomBar, CondensedTopBar itd.) używają useWiseTable() zamiast propsów.
 */

import React, { createContext, useContext, type ReactNode, type ReactElement } from 'react';
import type { IWiseTableProps } from 'components/wise/types';
import type { IWiseTableContextValue } from 'components/wise/WiseTable/context/types';
import { useWiseTableState } from 'components/wise/WiseTable/context/useWiseTableState';

export type { IWiseTableContextValue } from 'components/wise/WiseTable/context/types';

const WiseTableContext = createContext<IWiseTableContextValue | null>(null);

export interface WiseTableProviderProps {
  props: IWiseTableProps;
  children: ReactNode;
}

/**
 * Provider wstrzykuje stan tabeli (facade hook) do kontekstu.
 * Opakowuje drzewo WiseTable – używaj wewnątrz komponentu WiseTable.
 */
export function WiseTableProvider({ props, children }: WiseTableProviderProps): ReactElement {
  const value = useWiseTableState(props);
  return (
    <WiseTableContext.Provider value={value}>
      {children}
    </WiseTableContext.Provider>
  );
}

/**
 * Hook do odczytu kontekstu WiseTable. Wymaga użycia wewnątrz WiseTableProvider.
 */
export function useWiseTable(): IWiseTableContextValue {
  const ctx = useContext(WiseTableContext);
  if (ctx === null) {
    throw new Error('useWiseTable must be used within WiseTableProvider (inside WiseTable)');
  }
  return ctx;
}

